import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReadService } from './read.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { MessageService, SelectItem } from 'primeng';
import { ServerSentEventService } from './server-sent-event.service';
import { Observable, of } from 'rxjs';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import { PresignedUrl } from '../interfaces/pre-signed-url/pre-signed-url';

@Injectable({ providedIn: 'root' })
export class BillingService extends ReadService<any, any> {
  url = '/api/billing';
  entityTitle = '';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
    private serverSentEventService: ServerSentEventService,
  ) {
    super(http, handlersFactory);
  }

  processCustomerInvoices(
    plannedRunDay,
    runDelayedDays: number,
    tlinesMax: number,
    isDryRun: boolean,
    invoicePeriod: number,
  ): Observable<any> {
    return this.serverSentEventService.getServerSentEvent(
      `${
        this.url
      }/processCustomerInvoices?plannedRunDay=${plannedRunDay.toDateString()}&runDelayedDays=${runDelayedDays}&tlinesMax=${tlinesMax}&isDryRun=${isDryRun}&invoicePeriod=${invoicePeriod}`,
    );
  }

  processSupplierInvoices(
    plannedRunDay,
    runDelayedDays: number,
    isDryRun: boolean,
  ): Observable<any> {
    return this.serverSentEventService.getServerSentEvent(
      `${
        this.url
      }/processSupplierInvoices?plannedRunDay=${plannedRunDay.toDateString()}&runDelayedDays=${runDelayedDays}&isDryRun=${isDryRun}`,
    );
  }

  processBatches(plannedRunDay, runDelayedDays: number, isDryRun: boolean): Promise<any> {
    return this.http
      .get(
        `${
          this.url
        }/processBatches?plannedRunDay=${plannedRunDay.toDateString()}&runDelayedDays=${runDelayedDays}&isDryRun=${isDryRun}`,
        { headers: new HttpHeaders({ timeout: '600000' }) },
      )
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  processPayments(plannedRunDay, runDelayedDays: number, isDryRun: boolean): Observable<any> {
    return this.serverSentEventService.getServerSentEvent(
      `${
        this.url
      }/processPayments?plannedRunDay=${plannedRunDay.toDateString()}&runDelayedDays=${runDelayedDays}&isDryRun=${isDryRun}`,
    );
  }

  processXeroBatches(plannedRunDay, runDelayedDays: number, isDryRun: boolean): Promise<any> {
    return this.http
      .get(
        `${
          this.url
        }/processXeroBatches?plannedRunDay=${plannedRunDay.toDateString()}&runDelayedDays=${runDelayedDays}&isDryRun=${isDryRun}`,
        { headers: new HttpHeaders({ timeout: '600000' }) },
      )
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  resendFailedInvoice(invoiceId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/resendFailedInvoice?invoiceId=${invoiceId}`),
      this.handlersFactory.getForUpdate('Payments'),
    ).then(this.extractData);
  }

  customersDefSelect: SelectItem = { label: 'All Customers', value: 0 };
  public bindCustomers(): Promise<SelectItem[]> {
    return this.http
      .get<Array<any>>(`${this.url}/bindCustomers`)
      .toPromise()
      .then((res) => {
        var l = [this.customersDefSelect];
        if (res != null) {
          res.forEach((item) => {
            l.push({ label: item.name, value: item.id });
          });
        }
        return l;
      });
  }

  // return URL containing zip file for all order invoices related to the specified invoice
  generateOrderLevelInvoices(invoiceId: number): Promise<PresignedUrl> {
    return this.http
      .get<PresignedUrl>(`${this.url}/generate-order-level-invoices?invoiceId=${invoiceId}`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  setupRevolutWebhookUrl(): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/setRevolutWebhookUrl`),
      this.handlersFactory.getDefault(),
    ).then(this.extractData);
  }

  //for testing
  removeXeroData(): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeXeroData', { headers: new HttpHeaders({ timeout: '1000000' }) })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeCustomerInvoices(): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeCustomerInvoices', {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeCustomerInvoice(id: number): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeCustomerInvoice?id=' + id, {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeBatches(): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeBatches', { headers: new HttpHeaders({ timeout: '1000000' }) })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeBatch(id: number): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeBatch?id=' + id, {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }
  //for testing
  generateCustomerTransactions(customerId, from, days): Promise<any> {
    return this.http
      .get(
        `${
          this.url
        }/generateCustomerTransactions?customerId=${customerId}&from=${from.toDateString()}&days=${days}`,
        { headers: new HttpHeaders({ timeout: '1000000' }) },
      )
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeSupplierInvoices(): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeSupplierInvoices', {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  removeSupplierInvoice(id: number): Promise<any> {
    return this.http
      .get(`${this.url}` + '/removeSupplierInvoice?id=' + id, {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  generateSupplierTransactions(supplierId, from, days): Promise<any> {
    return this.http
      .get(
        `${
          this.url
        }/generateSupplierTransactions?supplierId=${supplierId}&from=${from.toDateString()}&days=${days}`,
        { headers: new HttpHeaders({ timeout: '1000000' }) },
      )
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  supplierDefSelect: SelectItem = { label: 'All Suppliers', value: 0 };
  public bindSuppliers(): Promise<SelectItem[]> {
    return this.http
      .get<Array<any>>(`${this.url}/bindSuppliers`)
      .toPromise()
      .then((res) => {
        var l = [this.supplierDefSelect];
        if (res != null) {
          res.forEach((item) => {
            l.push({ label: item.name, value: item.id });
          });
        }
        return l;
      });
  }

  //for testing
  emulateWebhookEvent(e: any): Promise<any> {
    return this.http
      .post(`${this.url}` + '/revolutWebhookEmulate', e)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  //for testing
  processAll(emulateToday): Promise<any> {
    return this.http
      .get(`${this.url}/processAll?emulateToday=${emulateToday.toDateString()}`, {
        headers: new HttpHeaders({ timeout: '1000000' }),
      })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public getLastMonthRange() {
    var year = new Date().getFullYear();
    var month = new Date().getMonth();

    return {
      from: new Date(year, month, 1),
      to: new Date(year, month, new Date(year, month + 1, 0).getDate()),
    };
  }

  isProcessInUse(): Promise<boolean> {
    return this.http
      .get(`${this.url}/isProcessInUse`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  downloadFileFromServer(url: string, filename: string): Promise<void> {
    return this.handleRequest(
      this.http.get(url, { responseType: 'blob' }),
      this.handlersFactory.getForReadAll(),
    )
      .then((result) => {
        this.handleDownloadFile(result, filename);
        this.messageService.add(
          Message.Success('Downloaded successfully. Check your download folder.'),
        );
      })
      .catch((err) => {
        console.error('Error downloading file:', err); // Log error details
        this.messageService.add(Message.Error('Could not download the file.'));
      });
  }

  public handleDownloadFile(blob: Blob, filename: string): void {
    FileSaver.saveAs(blob, filename);
  }
}
